import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CityCombobox from "@/components/CityCombobox";
import OtpDialog from "@/components/OtpDialog";
import { Camera, Banknote, MapPin, Clock } from "lucide-react";

export default function PartCreate() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [otpChannel, setOtpChannel] = useState(null);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cityObj, setCityObj] = useState(user?.city && user?.lat ? { name: user.city, dept: user.dept, lat: user.lat, lon: user.lon } : null);

  const [form, setForm] = useState({
    title: "", trade: "", description: "",
    budget_min: 500, budget_max: 1500,
    urgency: "1-semaine",
    photos: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/trades").then((r) => setTrades(r.data));
  }, []);

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    }))).then((arr) => set("photos", arr));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.trade || !form.description || !cityObj) {
      toast.error("Merci de remplir tous les champs obligatoires (dont ville)");
      return;
    }
    if (!user.verified_phone && !user.verified_email) {
      toast.error("Veuillez vérifier votre téléphone ou email avant de publier");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        ...form,
        city: cityObj.name, dept: cityObj.dept, area: null,
        lat: cityObj.lat, lon: cityObj.lon,
      });
      toast.success("Demande publiée — les artisans vont la recevoir");
      nav("/p/requests");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const needsVerif = !user.verified_phone && !user.verified_email;

  return (
    <AppShell role="particulier" title="Nouvelle demande">
      {needsVerif && (
        <div className="bg-brand-light text-brand border border-brand/30 rounded-2xl p-3 mb-4 text-sm">
          Vérifiez votre téléphone ou email pour publier vos demandes.
          <div className="flex gap-2 mt-2">
            <Button size="sm" className="rounded-full bg-brand hover:bg-brand-hover" onClick={() => setOtpChannel("email")} data-testid="verify-email-quick">Vérifier email</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOtpChannel("phone")} data-testid="verify-phone-quick">Vérifier tel</Button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Titre</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex: Refaire ma salle de bain" className="rounded-xl h-12" data-testid="create-title-input" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Type de prestation</Label>
          <Select value={form.trade} onValueChange={(v) => set("trade", v)}>
            <SelectTrigger className="rounded-xl h-12" data-testid="create-trade-select"><SelectValue placeholder="Choisir un métier" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Description du projet</Label>
          <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Détaillez vos travaux, dimensions, contraintes…" className="rounded-xl" data-testid="create-desc-input" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate2 flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> Budget estimé *</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <Input type="number" value={form.budget_min} onChange={(e) => set("budget_min", parseInt(e.target.value || 0))} placeholder="Min" className="rounded-xl h-12" data-testid="create-budget-min" />
            <Input type="number" value={form.budget_max} onChange={(e) => set("budget_max", parseInt(e.target.value || 0))} placeholder="Max" className="rounded-xl h-12" data-testid="create-budget-max" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate2 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Urgence *</Label>
          <div className="grid grid-cols-4 gap-2 mt-1.5">
            {[
              ["urgent", "Urgent"], ["1-semaine", "1 sem."], ["1-mois", "1 mois"], ["flexible", "Flex"]
            ].map(([k, l]) => (
              <button key={k} type="button" onClick={() => set("urgency", k)}
                className={`text-xs font-semibold py-3 rounded-xl tap-scale border ${form.urgency === k ? "bg-brand text-white border-brand" : "bg-white border-border text-slate2"}`}
                data-testid={`urgency-${k}`}
              >{l}</button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Ville *</Label>
          <CityCombobox value={cityObj} onChange={setCityObj} testid="create-city" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate2 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Photos (optionnel, max 3)</Label>
          <label className="block mt-1.5 border-2 border-dashed border-border rounded-2xl p-6 text-center tap-scale cursor-pointer">
            <input type="file" multiple accept="image/*" onChange={onPhotos} className="hidden" data-testid="create-photos-input" />
            <Camera className="w-6 h-6 mx-auto text-slate-400" />
            <div className="text-sm text-slate2 mt-2">{form.photos.length > 0 ? `${form.photos.length} photo(s) sélectionnée(s)` : "Ajouter des photos"}</div>
          </label>
          {form.photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {form.photos.map((p, i) => <img key={i} src={p} alt="" className="w-full h-20 object-cover rounded-xl" />)}
            </div>
          )}
        </div>

        <Button type="submit" disabled={busy || needsVerif} className="w-full rounded-full bg-brand hover:bg-brand-hover py-6 text-base font-semibold mt-4" data-testid="create-submit-btn">
          {busy ? "Publication…" : "Publier ma demande"}
        </Button>
      </form>

      <OtpDialog
        open={!!otpChannel}
        onOpenChange={(v) => { if (!v) setOtpChannel(null); }}
        channel={otpChannel || "email"}
        onVerified={refresh}
      />
    </AppShell>
  );
}
